<template>
  <v-container>
    <!-- Status and Permissions -->
    <v-row dense v-if="!isDelivery">
      <v-col cols="4">
        <v-switch
          v-model="employee.state"
          inset
          :label="$t('active')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="8" v-if="$route.params.parent_id || parentCompanies.length">
        <v-switch
          v-model="employee.is_transferable"
          inset
          :label="$t('change_location')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="4" v-if="employee.all_cooperation">
        <v-switch
          v-model="employee.can_change_restoran_company"
          inset
          :label="$t('can_change_restoran_company')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="12" v-if="employee.company?.no_time_limit">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-switch
                v-model="employee.no_time_limit_user"
                inset
                :label="$t('time_limit')"
                :false-value="0"
                :true-value="1"
                dense
                hide-details
              />
            </div>
          </template>
          <span>
            {{ $t("time_limit_tooltip_line1") }}<br />
            {{ $t("time_limit_tooltip_line2") }}
          </span>
        </v-tooltip>
      </v-col>
    </v-row>

    <!-- Company Selection -->
    <v-row dense v-if="!isDelivery">
      <v-col cols="12">
        <autocomplete
          v-if="employee.id"
          v-model="employee.company"
          :items="parentCompanies || []"
          :label="$t('current_company')"
          item-text="name"
          item-id="id"
          @change="$emit('open-change-company')"
          return-object
          persistent-hint
          :hide-details="false"
          :hint="$t('change_office_meals_note')"
        />
      </v-col>
    </v-row>

    <v-divider class="my-2" v-if="!isDelivery" />

    <!-- Basic Info -->
    <v-row dense>
      <v-col cols="6">
        <text-field
          v-model="employee.name"
          :rules="[rules.required]"
          :label="$t('name')"
          required
        />
      </v-col>
      <v-col cols="6">
        <text-field
          v-model="employee.lastname"
          :rules="[rules.required]"
          :label="$t('last_name')"
          required
        />
      </v-col>
    </v-row>
    <v-row dense v-if="!isDelivery">
      <v-col cols="6">
        <text-field
          v-model="employee.email"
          :rules="[rules.required]"
          :label="$t('email_or_username')"
          required
        />
      </v-col>
      <v-col cols="6">
        <text-field
          type="email"
          v-model="employee.private_email"
          :label="$t('private_email')"
        />
      </v-col>
    </v-row>

    <!-- Company/Office and Password -->
    <v-row dense>
      <v-col
        cols="6"
        v-if="
          !isDelivery &&
          $store.state.company &&
          ($store.state.company.children || []).length > 0 &&
          employee.id
        "
      >
        <dropdown
          :items="$store.state.company.children"
          :label="$t('office')"
          item-text="name"
          item-value="id"
          :rules="[$t('required_field')]"
          v-model="employee.company_id"
          dense
        />
      </v-col>
      <v-col cols="6" v-if="!isDelivery">
        <text-field
          v-model="employee.password"
          :label="$t('password')"
          type="password"
          :required="!employee.id"
          :rules="employee.id ? [] : [rules.required, rules.password]"
        />
      </v-col>
      <v-col cols="6">
        <employee-role-picker v-model="employee.type" :company="selectedCompany" />
      </v-col>
    </v-row>

    <!-- Contract switches for recipient -->
    <v-row dense v-if="employee.type === 'recipient'">
      <v-col cols="6">
        <v-switch
          v-model="employee.is_contract"
          inset
          :label="$t('is_contract')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="6">
        <v-switch
          v-model="employee.is_non_contract"
          inset
          :label="$t('is_non_contract')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
    </v-row>

    <!-- Offices for recipient -->
    <v-row dense v-if="employee.type == 'recipient' && companies[0]">
      <v-col cols="12">
        <autocomplete
          v-model="searchs"
          :items="companies[0].children.filter((i) => i.name)"
          dense
          filled
          multiple
          :label="$t('choose_office')"
          item-text="name"
          item-value="id"
          clear
          chips
          small-chips
        >
          <v-btn
            color="primary"
            slot="prepend-item"
            @click="$emit('update:searchs', [])"
            small
            class="mx-4"
          >
            {{ $t("display_all") }}
          </v-btn>
        </autocomplete>
      </v-col>
    </v-row>

    <v-divider class="my-2" />

    <!-- Address, Phone, WorkPos, External Code, Team -->
    <v-row dense>
      <v-col cols="6" v-if="!isDelivery">
        <text-field v-model="employee.address" :label="$t('address')" dense />
      </v-col>
      <v-col :cols="isDelivery ? 12 : 6">
        <text-field v-model="employee.phone" :label="$t('phone')" dense />
      </v-col>
      <v-col cols="6" v-if="!isDelivery">
        <text-field v-model="employee.work_position" :label="$t('work_place')" dense />
      </v-col>
      <v-col cols="6" v-if="!isDelivery">
        <text-field v-model="employee.external_code" :label="$t('external_id')" dense />
      </v-col>
      <v-col cols="6" v-if="!isDelivery">
        <text-field :label="$t('team')" v-model="employee.team" type="number" dense />
      </v-col>
      <v-col cols="6" v-if="!isDelivery">
        <autocomplete
          :label="$t('language')"
          v-model="employee.lang"
          type="number"
          dense
          :items="availableLanguages"
          item-text="text"
          item-value="code"
        />
      </v-col>
    </v-row>

    <v-divider class="my-2" />

    <!-- Dates -->
    <v-row dense>
      <v-col cols="12" class="mb-0 pb-0">
        <p class="text-left font-weight-bold">{{ $t("account_creation_edit_date") }}:</p>
      </v-col>
      <v-col cols="6" :title="$t('registration_date')">
        <v-icon class="mr-2">mdi-calendar</v-icon>
        {{ moment(employee.created_at).format("DD.MM.YYYY. H:mm:ss") }}
      </v-col>
      <v-col cols="6" :title="$t('modification_date')">
        <v-icon class="mr-2">mdi-calendar-sync</v-icon>
        {{ moment(employee.updated_at).format("DD.MM.YYYY. H:mm:ss") }}
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import hasValidation from "@/mixins/hasValidation";
import EmployeeRolePicker from "@/components/company/fields/EmployeeRolePicker.vue";

export default {
  mixins: [hasValidation],
  name: "EmployeeFormSection",
  props: {
    employee: Object,
    parentCompanies: Array,
    companies: Array,
    errorMessageEmail: String,
    searchs: Array,
    selectedCompany: {
      type: Object,
      required: false,
    },
    // Delivery users only support name / lastname / phone / type
    // (PATCH .../users-delivery/:id). Hide every other field and, importantly,
    // avoid touching $store.state.company.children — a delivery firm object
    // (from /user/me companies[]) has no `children`.
    isDelivery: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EmployeeRolePicker,
  },
  data() {
    return {
      moment,
    };
  },
  computed: {
    availableLanguages() {
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];
      if (restaurantLanguages.length === 0) {
        return this.getAvailableLanguages();
      }
      return this.getAvailableLanguages().filter((lang) =>
        restaurantLanguages.includes(lang.code)
      );
    },
  },
  emits: ["open-change-company", "update:searchs"],
};
</script>

<style>
/* Hide scrollbar for the dialog card content */
.ad::-webkit-scrollbar {
  display: none;
}
.ad .v-container::-webkit-scrollbar {
  display: none;
}
</style>
